import React, { useEffect, useMemo, useRef, useState } from "react";
import { SupervisorLayout } from "@/layouts/SupervisorLayout";
import client, { API, BACKEND_ORIGIN, resolveApiUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Users, Plus, Camera, Loader2, ShieldCheck, ShieldAlert, ShieldQuestion,
  Radio, AlertTriangle, Pencil, Trash2, UserRound,
} from "lucide-react";

const LEVELS = [
  { value: "LOW",      label: "Low",      icon: ShieldQuestion, color: "rgba(255,255,255,0.55)" },
  { value: "MEDIUM",   label: "Medium",   icon: ShieldAlert,    color: "#f5b544" },
  { value: "ADVANCED", label: "Advanced", icon: ShieldCheck,    color: "var(--accent)" },
];

const levelMeta = (v) => LEVELS.find((l) => l.value === v) || LEVELS[1];

const ERROR_HINTS = {
  current_station_active_build: "This technician's current station has a LIVE or PAUSED build — end it first.",
  target_station_active_build:  "Target station has a LIVE or PAUSED build. Wait for it to finish.",
  target_station_already_assigned: "Target station is already assigned to another technician.",
  target_station_not_found: "Target station does not exist yet.",
  name_required: "Name is required.",
  invalid_level: "Choose a valid level.",
  technician_not_found: "Technician not found.",
  unsupported_image_type: "Only JPG, PNG or WEBP images are allowed.",
  image_too_large: "Image is larger than 5 MB.",
  empty_image: "Selected image is empty.",
  storage_unavailable: "Photo storage is temporarily unavailable.",
  storage_upload_failed: "Failed to upload photo. Please retry.",
};
const hint = (raw) => {
  const key = typeof raw === "string" ? raw.split(":")[0] : "";
  return ERROR_HINTS[key] || formatApiError(raw);
};

function LevelBadge({ level }) {
  const m = levelMeta(level);
  const Icon = m.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] tracking-[0.14em] font-medium uppercase"
      style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", color: m.color }}
      data-testid={`tech-level-${m.value}`}
    >
      <Icon size={11} /> {m.label}
    </span>
  );
}

function StationChip({ stationId, busy }) {
  if (!stationId) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] tracking-[0.14em] font-medium uppercase text-white/45"
        style={{ background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.12)" }} data-testid="tech-station-unassigned">
        Unassigned
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] tracking-[0.14em] font-medium uppercase"
      style={{ background: busy ? "rgba(255,51,51,0.10)" : "rgba(0,229,255,0.10)", border: `1px solid ${busy ? "rgba(255,51,51,0.35)" : "rgba(0,229,255,0.25)"}`, color: busy ? "#ff8080" : "var(--accent)" }}
      data-testid={`tech-station-${stationId}`}>
      {busy && <Radio size={10} />} {stationId}
    </span>
  );
}

function TechCard({ tech, busyStations, onEdit, onDelete }) {
  const active = tech.active;
  const busy = tech.station_id && busyStations.has(tech.station_id);
  const initials = (tech.name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join("");
  const photoSrc = resolveApiUrl(tech.profile_photo_url);
  return (
    <div className="rounded-xl border p-5 flex flex-col gap-4 transition-colors hover:border-white/15"
      style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0d0d10", opacity: active ? 1 : 0.55 }}
      data-testid={`tech-card-${tech.tech_id}`}>
      <div className="flex items-start gap-4">
        <div className="w-14 h-14 rounded-full flex items-center justify-center shrink-0 overflow-hidden"
          style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(255,255,255,0.08)", color: "var(--accent)" }}>
          {photoSrc
            ? <img src={photoSrc} alt={tech.name} className="w-full h-full object-cover" onError={(e)=>{e.currentTarget.style.display='none';}} />
            : initials ? <span className="text-base font-semibold">{initials}</span> : <UserRound size={22}/>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[15px] font-semibold text-white truncate" data-testid={`tech-name-${tech.tech_id}`}>{tech.name}</div>
            {!active && <span className="text-[9px] tracking-[0.2em] font-medium text-white/45 uppercase" data-testid={`tech-inactive-${tech.tech_id}`}>Inactive</span>}
          </div>
          <div className="text-[11px] tracking-[0.18em] font-medium text-white/45 uppercase mt-0.5" data-testid={`tech-id-${tech.tech_id}`}>{tech.tech_id}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <LevelBadge level={tech.level} />
        <StationChip stationId={tech.station_id} busy={busy} />
      </div>
      <div className="mt-auto pt-3 flex items-center justify-end gap-2 border-t" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
        <Button variant="ghost" size="sm" className="text-white/60 hover:text-white h-8 px-3 font-medium"
          onClick={() => onEdit(tech)} data-testid={`tech-edit-${tech.tech_id}`}>
          <Pencil size={13} className="mr-1.5" /> Edit
        </Button>
        <Button variant="ghost" size="sm" className="text-white/40 hover:text-[#ff6b6b] h-8 px-3 font-medium"
          onClick={() => onDelete(tech)} disabled={busy} data-testid={`tech-delete-${tech.tech_id}`} title={busy ? "Active build in progress" : "Remove technician"}>
          <Trash2 size={13} />
        </Button>
      </div>
    </div>
  );
}

function TechnicianModal({ open, onOpenChange, editing, stations, busyStations, techsByStation, onSaved }) {
  const isEdit = !!editing;
  const [name, setName] = useState("");
  const [level, setLevel] = useState("MEDIUM");
  const [stationId, setStationId] = useState("");
  const [active, setActive] = useState(true);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDeactivateBusy, setConfirmDeactivateBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name || "");
      setLevel(editing.level || "MEDIUM");
      setStationId(editing.station_id || "");
      setActive(editing.active !== false);
      setPhotoPreview(resolveApiUrl(editing.profile_photo_url));
    } else {
      setName(""); setLevel("MEDIUM"); setStationId(""); setActive(true); setPhotoPreview("");
    }
    setPhotoFile(null);
    setConfirmDeactivateBusy(false);
  }, [open, editing]);

  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg","image/jpg","image/png","image/webp"].includes(f.type)) { toast.error("Only JPG, PNG or WEBP"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image must be ≤ 5 MB"); return; }
    setPhotoFile(f);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(String(reader.result || ""));
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      let saved;
      if (isEdit) {
        const patch = {
          name: trimmed,
          level,
          station_id: stationId || null,
          active,
        };
        const { data } = await client.patch(`/technicians/${editing.id}`, patch);
        saved = data;
      } else {
        const { data } = await client.post("/technicians", {
          name: trimmed, level, station_id: stationId || null, active,
        });
        saved = data;
      }
      if (photoFile) {
        const fd = new FormData();
        fd.append("photo", photoFile);
        const { data } = await client.post(`/technicians/${saved.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        saved = data;
      }
      toast.success(isEdit ? "Technician updated" : "Technician created");
      onSaved(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(hint(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Station picker: options with reason-locked variants
  const stationOptions = useMemo(() => {
    const opts = [{ value: "", label: "Unassigned", disabled: false }];
    for (const s of stations) {
      const sid = s.id;
      const busy = busyStations.has(sid);
      const otherTech = techsByStation.get(sid);
      const takenByOther = otherTech && otherTech.id !== editing?.id;
      let reason = "";
      if (busy) reason = " · active build";
      else if (takenByOther) reason = ` · ${otherTech.name}`;
      opts.push({
        value: sid,
        label: `${sid}${reason}`,
        disabled: busy || takenByOther,
      });
    }
    return opts;
  }, [stations, busyStations, techsByStation, editing]);

  const deactivatingBusy = isEdit && editing?.active && !active && editing.station_id && busyStations.has(editing.station_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-white/10" style={{ background: "#0a0a0d", color: "#fff" }} data-testid="tech-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold tracking-tight">{isEdit ? `Edit ${editing.tech_id}` : "Add Technician"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 space-y-5">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full overflow-hidden flex items-center justify-center shrink-0"
              style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(255,255,255,0.10)" }}>
              {photoPreview
                ? <img src={photoPreview} alt="preview" className="w-full h-full object-cover" />
                : <UserRound size={30} className="text-white/40" />}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={pickPhoto} className="hidden" data-testid="tech-photo-input" />
              <Button type="button" variant="ghost" onClick={() => fileRef.current?.click()} className="text-white/80 hover:text-white h-9 px-3 font-medium" data-testid="tech-photo-button">
                <Camera size={14} className="mr-1.5" /> {photoPreview ? "Change photo" : "Upload photo"}
              </Button>
              <div className="text-[11px] text-white/40 mt-1 font-normal">JPG · PNG · WEBP · max 5 MB</div>
            </div>
          </div>

          <div>
            <Label className="text-[11px] tracking-[0.2em] text-white/55 uppercase font-medium">Full Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ahmed Al-Balushi"
              className="mt-2 h-10 bg-black border-white/15 text-white" data-testid="tech-name-input" />
          </div>

          <div>
            <Label className="text-[11px] tracking-[0.2em] text-white/55 uppercase font-medium">Level</Label>
            <div className="mt-2 grid grid-cols-3 gap-2" data-testid="tech-level-picker">
              {LEVELS.map((l) => {
                const on = level === l.value;
                const Icon = l.icon;
                return (
                  <button key={l.value} type="button" onClick={() => setLevel(l.value)} data-testid={`tech-level-opt-${l.value}`}
                    className={`h-10 rounded-md text-[12px] tracking-[0.14em] uppercase font-medium border transition-colors flex items-center justify-center gap-1.5 ${on ? "text-white" : "text-white/50 hover:text-white/80"}`}
                    style={{
                      background: on ? "rgba(0,229,255,0.12)" : "rgba(255,255,255,0.02)",
                      borderColor: on ? "rgba(0,229,255,0.45)" : "rgba(255,255,255,0.10)",
                    }}>
                    <Icon size={12} style={{ color: on ? l.color : undefined }} /> {l.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-[11px] tracking-[0.2em] text-white/55 uppercase font-medium">Station Assignment</Label>
            <select value={stationId} onChange={(e) => setStationId(e.target.value)} data-testid="tech-station-select"
              className="mt-2 w-full h-10 rounded-md bg-black border border-white/15 text-white px-3 text-sm font-normal focus:outline-none">
              {stationOptions.map((o) => (
                <option key={o.value || "unassigned"} value={o.value} disabled={o.disabled}>{o.label}</option>
              ))}
            </select>
            <div className="text-[11px] text-white/40 mt-1.5 flex items-center gap-1 font-normal">
              <AlertTriangle size={11} /> Stations with an active LIVE/PAUSED build cannot be reassigned.
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg px-4 py-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div>
              <div className="text-sm font-medium">Active</div>
              <div className="text-[11px] text-white/45 font-normal">Inactive technicians are hidden from station pickers.</div>
            </div>
            <button type="button" onClick={() => setActive((v) => !v)} data-testid="tech-active-toggle"
              className="relative w-11 h-6 rounded-full transition-colors"
              style={{ background: active ? "var(--accent)" : "rgba(255,255,255,0.15)" }}
              aria-pressed={active}>
              <span className="absolute top-0.5 h-5 w-5 rounded-full bg-black transition-all" style={{ left: active ? "22px" : "2px" }} />
            </button>
          </div>

          {deactivatingBusy && (
            <div className="rounded-md px-3 py-2 text-[12px] flex items-center gap-2"
              style={{ background: "rgba(255,51,51,0.08)", border: "1px solid rgba(255,51,51,0.25)", color: "#ff9c9c" }}
              data-testid="tech-deactivate-warning">
              <AlertTriangle size={13}/> Cannot deactivate — station {editing.station_id} has an active build.
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} className="text-white/70 hover:text-white font-medium" data-testid="tech-modal-cancel">Cancel</Button>
            <Button type="submit" disabled={saving || deactivatingBusy} data-testid="tech-modal-save"
              className="h-10 rounded-sm font-medium" style={{ background: "var(--accent)", color: "#000" }}>
              {saving ? <><Loader2 className="animate-spin mr-1.5" size={14}/> Saving…</> : isEdit ? "Save changes" : "Create technician"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteConfirm({ tech, onClose, onConfirm, deleting }) {
  return (
    <Dialog open={!!tech} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md border" style={{ background: "#0a0a0d", color: "#fff", borderColor: "rgba(255,51,51,0.35)" }} data-testid="tech-delete-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            <AlertTriangle size={18} style={{ color: "#ff3333" }}/> Remove technician?
          </DialogTitle>
        </DialogHeader>
        <p className="text-white/70 text-sm leading-relaxed font-normal">
          <span className="text-white font-semibold">{tech?.name}</span> ({tech?.tech_id}) will be unassigned from any station and marked inactive.
          Their name will remain on completed builds for historical accuracy.
        </p>
        <DialogFooter className="gap-2 mt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={deleting} className="text-white/70 hover:text-white font-medium" data-testid="tech-delete-cancel">Cancel</Button>
          <Button type="button" onClick={onConfirm} disabled={deleting} className="h-10 rounded-sm font-medium" style={{ background: "#ff3333", color: "#fff" }} data-testid="tech-delete-confirm">
            {deleting ? <Loader2 className="animate-spin mr-1.5" size={14}/> : <Trash2 size={14} className="mr-1.5"/>} Remove
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TechniciansPage() {
  const [techs, setTechs] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [busyStations, setBusyStations] = useState(new Set());

  const load = async () => {
    try {
      const [techsRes, stationsRes, ordersRes] = await Promise.all([
        client.get("/technicians"),
        client.get("/stations"),
        client.get("/orders"),
      ]);
      setTechs(techsRes.data || []);
      setStations(stationsRes.data || []);
      const busy = new Set();
      (ordersRes.data || []).forEach((o) => {
        if (o.status === "LIVE" || o.status === "PAUSED") busy.add(o.station_id);
      });
      setBusyStations(busy);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load technicians");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, []);

  const techsByStation = useMemo(() => {
    const m = new Map();
    techs.forEach((t) => { if (t.station_id) m.set(t.station_id, t); });
    return m;
  }, [techs]);

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await client.delete(`/technicians/${deleteTarget.id}`);
      toast.success("Technician removed");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(hint(e.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <SupervisorLayout>
      <div className="p-5 md:p-8 max-w-[1600px]">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.28em] font-semibold text-white/45 uppercase flex items-center gap-2">
              <Users size={12}/> People
            </div>
            <h1 className="mt-2 text-2xl md:text-3xl font-semibold text-white tracking-tight" data-testid="technicians-heading">Technicians</h1>
            <p className="mt-1.5 text-white/50 text-sm max-w-xl font-normal">
              Manage technician profiles, levels and station assignments. Active builds are safely locked from reassignment.
            </p>
          </div>
          <Button onClick={() => setAddOpen(true)} data-testid="add-technician-button"
            className="h-10 rounded-sm font-medium" style={{ background: "var(--accent)", color: "#000" }}>
            <Plus size={15} className="mr-1"/> Add Technician
          </Button>
        </div>

        {loading ? (
          <div className="rounded-xl border py-16 text-center text-white/50 flex items-center justify-center gap-2"
            style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0a0a0d" }} data-testid="tech-loading">
            <Loader2 className="animate-spin" size={16}/> Loading technicians…
          </div>
        ) : techs.length === 0 ? (
          <div className="rounded-xl border py-16 text-center flex flex-col items-center gap-3"
            style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0a0a0d" }} data-testid="tech-empty">
            <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "rgba(0,229,255,0.08)", color: "var(--accent)" }}>
              <Users size={22}/>
            </div>
            <div className="text-white font-semibold text-base">No technicians yet</div>
            <div className="text-white/45 text-sm font-normal">Add your first technician to assign them to Station 1.</div>
            <Button onClick={() => setAddOpen(true)} className="mt-2 h-9 rounded-sm font-medium" style={{ background: "var(--accent)", color: "#000" }} data-testid="add-first-technician">
              <Plus size={14} className="mr-1"/> Add Technician
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="tech-grid">
            {techs.map((t) => (
              <TechCard key={t.id} tech={t} busyStations={busyStations}
                onEdit={(x) => setEditing(x)} onDelete={(x) => setDeleteTarget(x)} />
            ))}
          </div>
        )}
      </div>

      <TechnicianModal open={addOpen} onOpenChange={setAddOpen} editing={null}
        stations={stations} busyStations={busyStations} techsByStation={techsByStation}
        onSaved={() => load()} />
      <TechnicianModal open={!!editing} onOpenChange={(o) => !o && setEditing(null)} editing={editing}
        stations={stations} busyStations={busyStations} techsByStation={techsByStation}
        onSaved={() => load()} />
      <DeleteConfirm tech={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={doDelete} deleting={deleting} />
    </SupervisorLayout>
  );
}
